/* eslint-disable react/no-unescaped-entities */
const PrivacyPolicy = () => (
  <div>
    <h2>Carson Privacy Policy</h2>

    <h4>Last Modified: May 2021</h4>

    <p>
      Any references to "Carson," "we,” “our” or “us” refer to Carson Living,
      Inc. Carson respects your privacy as a user of this site. This Privacy
      Policy explains what personal information we collect from you, how we use
      that information and our legal basis for doing so. This policy also
      details the choices available to you regarding our use of your personal
      information as well as how you can access and update this information.
      This Privacy Policy will be easy to find on the Carson public homepage and
      on any page where we require information for use of the site.
    </p>

    <h4>What services does the Privacy Policy cover? </h4>

    <p>
      This policy applies to all Carson products including all Carson apps, as
      well as the Carson website (
      <a
        data-auto-recognition="true"
        href="http://www.Carson.live"
        target="_blank"
        rel="noreferrer"
      >
        www.Carson.live
      </a>
      ) and software (collectively the Carson "Platform”).
    </p>

    <h4>Customers and Users </h4>

    <p>
      Our “Customer” is typically a legal entity representing a building or
      property. Customers purchase or license one or more products or services
      that are part of the Platform. You are a user of the Platform. Carson
      collects information from our Customers and users on behalf of our
      Customers. There are many types of users, and each one has a unique
      relationship to the information that we collect. Users can include our
      Customers’ employees (building staff, maintenance personnel, managers,
      etc.), residents (tenants, owners, subtenants, guests, etc.), service
      providers (outside contractors, local businesses, etc.) and others. Each
      of these (and generally anyone who has a Carson username and password) is
      considered a “Registered User”. Some users may not be Registered Users,
      such as a visitor to our public website. Most Registered Users will be
      affiliated with one or more Carson Customers (i.e., your buildings).
      Carson is the custodian of data on behalf of our Customers, with respect
      to much of the information we collect. Each Customer also determines its
      own policies regarding information collection, access, sharing and
      retention. Please contact the Customer if you have any questions regarding
      their privacy practices.
    </p>

    <h4>The Information we collect</h4>

    <p>
      In order for you to use the Carson platform, we need to collect and
      process certain information (such as your name or address) and anonymous
      information (such as your IP address). Most of this information is
      provided by our Customers or by you, when you use our Platform. Some
      information is obtained through your use of our Platform, for example,
      when we receive error reports or use cookies (see below).
    </p>

    <p>
      Finally, we receive some information from other Registered Users. An
      example would be when a Registered User submits or modifies the
      information and settings for various non-member 3rd parties, such as
      family members, roommates, or other people who are relevant to the
      building’s management and who they wish to submit through Carson (e.g.
      housekeepers, cleaners, caretakers). There may also be personal
      information submitted by our Customers about our Registered Users that
      Registered Users cannot access directly.
    </p>

    <p>
      The types of information we collect depend on your level of access and
      engagement with the Carson Platform and can include:
    </p>

    <ul>
      <li>
        <p>
          Contact information such as names, email addresses, mailing addresses,
          phone numbers
        </p>
      </li>
      <li>
        <p>
          Unique identifiers such as usernames, passwords, Carson account
          numbers
        </p>
      </li>
      <li>
        <p>
          Basic information submitted by you or on your behalf such as service
          request descriptions
        </p>
      </li>
      <li>
        <p>
          Information uploaded by you such as documents attached to the Carson
          Platform
        </p>
      </li>
      <li>
        <p>
          If you choose to use sign in services such as Facebook Connect, these
          services will authenticate your identity and provide you the option to
          share certain personal information with us such as your name and email
          address
        </p>
      </li>
      <li>
        <p>
          If you choose to use Carson's single sign-on (SSO) with 3rd party
          vendors such as Clickpay or Aptexx, these services will authenticate
          your identify and provide you the option to access such 3rd party
          services such as Clickpay or Aptexx services. Carson does not store,
          transmit, nor process your payment or other personal information.
          Clickpay and Aptexx and similar 3rd party vendors process your payment
          information or other information in accordance with their respective
          privacy policies,{' '}
          <a
            href="https://site.clickpay.com/privacy-policy/"
            target="_blank"
            rel="noreferrer noopener"
          >
            here
          </a>{' '}
          and{' '}
          <a
            href="https://aptexx.com/privacy-policy/"
            target="_blank"
            rel="noreferrer noopener"
          >
            here
          </a>
          .
        </p>
      </li>
      <li>
        <p>
          Your communications with us, such as your requests for customer
          support
        </p>
      </li>
      <li>
        <p>
          Information about your usage such as IP addresses, browser type,
          date/time stamp and clickstream data
        </p>
      </li>
      <li>
        <p>
          Employment information for Customer employees (including title, name,
          phone number)
        </p>
      </li>
      <li>
        <p>
          Photographs (of residents, employees, pets, visitors, maintenance
          request attachments)
        </p>
      </li>
      <li>
        <p>Scans (license, business card) in the context of building</p>
      </li>
      <li>
        <p>Incident details and information</p>
      </li>
      <li>
        <p>
          Resident instructions (including permission to enter, permission for
          authorized visitors, intercom call settings)
        </p>
      </li>
    </ul>

    <h4>Cookies</h4>

    <p>
      We use cookies on this site to provide users with customized and
      personalized services. Cookies are small amounts of data that are sent to
      a user’s browser from a website and stored on a user’s hard drive for
      record-keeping purposes. This information does not necessarily identify
      the user, but identifies the user’s computer. The cookies used will not
      collect any personal information like name, address or telephone number.
    </p>

    <h5>Our use of cookies and other tracking technologies</h5>

    <p>
      We use both session ID cookies and persistent cookies. A session ID cookie
      expires when you close your browser. A persistent cookie remains on your
      hard drive for an extended period of time. You can remove persistent
      cookies by following directions provided in your Internet browser's "help"
      directory. If you reject cookies, you may still use our site, but your
      ability to use some areas of our site will be limited.
    </p>

    <h5>Web Beacons / GIFs</h5>

    <p>
      Third party tracking technology companies employ a software technology
      called clear gifs (a.k.a. Web Beacons), that help us better manage content
      on our site by informing us what content is effective. Clear gifs are tiny
      graphics with a unique identifier, similar in function to cookies, and are
      used to track the online movements of Web users. In contrast to cookies,
      which are stored on a user's computer hard drive, clear gifs are embedded
      invisibly on Web pages and are about the size of the period at the end of
      this sentence. We do not tie the information gathered by clear gifs to our
      customers' personally identifiable information.
    </p>

    <h5>Analytics / Log Files </h5>

    <p>
      As is true of most web sites, we gather certain information automatically
      and store it in log files. This information includes internet protocol
      (IP) addresses, browser type, internet service provider (ISP),
      referring/exit pages, operating system, date/time stamp, and clickstream
      data. We use this information, which does not identify individual users,
      to analyze trends, to administer the site, to track users' movements
      around the site and to gather demographic information about our user base
      as a whole. We do not link this automatically collected data to personally
      identifiable information.
    </p>

    <h5>3rd Party Tracking</h5>

    <p>
      The use of tracking technologies by our service providers, technology
      partners or other 3rd party assets (such as social media widgets and
      plug-ins) on the site is not covered by our privacy policy. These 3rd
      parties may use cookies, clear gifs, images, and scripts to help them
      better manage their content on our site. We do not have access or control
      over these technologies. We do not tie the information gathered to our
      Customers' or Users' personally identifiable information.
    </p>

    <h4>Legal Bases for Collecting Information</h4>

    <p>
      We process personal data for, or based on, one or more of the following
      legal bases:
    </p>

    <p>
      <h5>Performance of a Contract</h5>
      We use personal data to enter into and to perform under, an agreement
      between Carson and the Customer.
    </p>

    <p>
      <h5>Legitimate Interests</h5>
      We use personal data for our legitimate interests, including contact
      information, services offered, and website usage data.
    </p>

    <p>
      <h5>C ompliance with Legal Obligations and Protection of Individuals</h5>
    </p>

    <p>
      We may use personal data to comply with the law and our legal obligations,
      as well as to protect you and other individuals from certain harms.
    </p>

    <p>
      <h5>Your Consent</h5>
      We process certain personal data because you have given us your consent to
      process it in that manner.
    </p>

    <h4>Why We Collect Your Information</h4>

    <p>
      Carson uses the information we collect from all our Services for the
      following purposes:
    </p>

    <h5>Customize Our Services</h5>

    <p>
      We use your information to deliver our services, and customize for each
      Registered User or Customer.
    </p>

    <h5>Maintain &amp; Improve Our Services</h5>

    <p>
      We also use your information to ensure our Platform is working as
      intended, such as tracking outages or troubleshooting issues that you
      report to us. And we use your information to make improvements to our
      services.
    </p>

    <h5>Provide Personalized Services</h5>

    <p>We use the information we collect to customize our services for you.</p>

    <h5>Measure Performance</h5>

    <p>
      We use data for analytics and measurement to understand how our services
      are used. For example, we analyze data about how much time you spend
      accessing the Platform to do things like optimize Platform design.
    </p>

    <h5>Communicate with You</h5>

    <p>
      We use information we collect, like your email address, to interact with
      you directly. For example, we may send you a notification if we detect
      suspicious activity, like an attempt to sign into your Carson account from
      an unusual location. Or we may let you know about upcoming changes or
      improvements to our services. And if you contact Carson, we’ll keep a
      record of your request in order to help solve any issues you might be
      facing.
    </p>

    <h5>Protect Carson, Our Users, and The Public</h5>

    <p>
      We use information to help improve the safety and reliability of our
      services. This includes detecting, preventing, and responding to fraud,
      abuse, security risks, and technical issues that could harm Carson, our
      Customers, Registered Users, or the public.
    </p>

    <p>
      We use different technologies to process your information for these
      purposes. We use automated systems that analyze your content to provide
      you with things like customized services, or other features tailored to
      how you use our Services. And we analyze your content to help us detect
      abuse such as spam, malware, and illegal content.
    </p>

    <p>
      We may combine the information we collect among our Platform and across
      your devices for the purposes described above. If other Registered Users
      already have your email address or other information that identifies you,
      we may show them your publicly visible Carson account information, such as
      your name and photo. This helps people identify an email coming from you,
      for example.
    </p>

    <p>
      We’ll ask for your consent before using your information for a purpose
      that isn’t covered in this Privacy Policy.
    </p>

    <h4>How we use the information</h4>

    <p>For users of the Platform, we use the information we receive to:</p>

    <ul>
      <li>
        <p>Provide the services our Customer requested</p>
      </li>
      <li>
        <p>
          Notify you of activity related to your account, such as when a package
          is delivered to the property for you
        </p>
      </li>
      <li>
        <p>Administer your account</p>
      </li>
      <li>
        <p>
          Keep your account secure (including to verify your identity and
          prevent fraud)
        </p>
      </li>
      <li>
        <p>Respond to customer service and support requests</p>
      </li>
      <li>
        <p>
          Create and provide better and improved products and services based on
          your use of the site and your feedback
        </p>
      </li>
      <li>
        <p>
          List you in our member directory made available only to other members
          of your building
        </p>
      </li>
      <li>
        <p>Conduct research and analysis</p>
      </li>
      <li>
        <p>Send you our newsletter, which you may opt out of at any time</p>
      </li>
    </ul>

    <p>
      For visitors requesting information about our products and services from
      one of our websites, we use the information we receive to:
    </p>

    <ul>
      <li>
        <p>Assess the needs of your business to determine suitable products</p>
      </li>
      <li>
        <p>Send you requested product or service information</p>
      </li>
      <li>
        <p>Send you marketing communications</p>
      </li>
      <li>
        <p>Send you our newsletter, which you may opt out of at any time</p>
      </li>
    </ul>

    <p>
      If you provide us personal information about others, or if others give us
      your information, we will only use that information for the specific
      reason for which it was provided to us.
    </p>

    <h4>California Residents' Privacy Notice&nbsp;</h4>

    <p>
      The following user access and choice rights are obligations of Carson
      where we are the "business" under the CCPA (i.e., with respect to the
      personal information described above under the caption "Personal
      information we process as a controller or business"). With respect to all
      other personal information described herein, the access and choice rights
      are properly addressed between you and our relevant Customer subject to
      such Customer's privacy policy.
    </p>

    <p>
      Residents of the State of California may request information regarding the
      categories of personal information we gather about you, the categories of
      sources from which we obtain the personal information, the business
      purposes for which the personal information is gathered, and the
      categories of third parties with which we share or, in the event we ever
      sell personal information, by category of personal information sold to
      such parties, respectively.
    </p>

    <p>
      Upon verifiable request or when required or otherwise appropriate, and
      within periods (if any) set by applicable law, we will grant you
      reasonable access to or copies or other compilations of the specific items
      of personal information that we hold about you provided that you establish
      to our reasonable satisfaction that you are the person whose personal
      information is requested. We may deny such access where the denial is
      permitted by applicable law and every request from an individual will be
      assessed on a case by case basis. In the event a request is denied, we
      will notify you regarding the reasons for the denial in writing.
      Consistent with how your personal information is maintained in the
      ordinary course of our business, we will provide the information in an
      understandable form, and to the extent feasible in a format that permits
      you to use the information on other systems. We may impose a reasonable
      charge when a request is made (e.g., for photocopying or postage) to the
      extent permitted under applicable law.
    </p>

    <p>
      We will take reasonable steps to permit you to correct or amend personal
      information that is demonstrated to be inaccurate or incomplete. We also
      will delete personal information we have gathered about you when you make
      a verifiable request that we do so, except to the extent applicable law
      permits or requires us to maintain that information.
    </p>

    <p>
      To guard against fraudulent requests for access, we will require
      sufficient information to allow us to confirm the identity of the
      individual making the request before granting access or deleting the
      information. We will not discriminate against consumers who exercise their
      rights under California law. However, we may charge different prices or
      provide a different quality of goods or services if the difference is
      reasonably related to the value provided by your personal information.
    </p>

    <p>
      California's Do Not Track Notice: At this time, there is no worldwide
      uniform or consistent industry standard or definition for responding to,
      processing, or communicating Do Not Track signals. Thus, like many other
      websites and online services, our Platform is currently unable to respond
      to Do Not Track signals. To find out more about "Do Not Track", you may
      wish to visit&nbsp;
      <a
        href="http://www.allaboutdnt.com/"
        target="_blank"
        rel="noreferrer noopener"
      >
        http://www.allaboutdnt.com
      </a>
      .
    </p>

    <h4>European Union Users</h4>

    <p>
      Data protection law in Europe requires a “lawful basis” for collecting,
      processing and transferring personal information from citizens or
      residents of the European Economic Area (EEA). Our lawful bases, as
      detailed above, include performance of our contract with our
      <br />
      Customers, user consent (which is always easy to withdraw), and our
      legitimate interests (except where such interests are overridden by the
      interests or fundamental rights and freedoms of our EU users).
    </p>

    <p>
      For our E.U. users: We participate in the E.U.-U.S. Privacy Shield (see
      statement below), and this privacy policy indicates our compliance with
      the Principles of the Privacy Shield. Accordingly, Carson is responsible
      for the processing of information we receive pursuant to the Privacy
      Shield Framework, and subsequently transfer to a third party acting on our
      behalf (e.g. a service provider). It is our responsibility to ensure that
      any service providers we use also adhere to the Principles.
    </p>

    <h4>Service Providers</h4>

    <p>
      We share your personal information only with your consent or as necessary
      to complete any transaction or provide any product you have requested or
      authorized. We may provide your personal information to companies that
      provide services to help us deliver our services such as processing
      payments (Stripe), hosting applications or fulfilling other functions of
      the website. These third-party service providers are limited to accessing
      or using this information solely for the purpose of providing services to
      us and to you and must provide reasonable assurances that they will
      appropriately safeguard all information. For example, we transfer
      anonymous phone numbers to our communications provider Twilio, which
      completes calls and text messages for our Virtual Doorman service and
      building notifications. You can view Twilio’s Privacy Policy{' '}
      <a
        href="https://www.twilio.com/legal/privacy"
        target="_blank"
        rel="noreferrer noopener"
      >
        here
      </a>
      .
    </p>

    <h4>Aggregated Information</h4>

    <p>
      Your personal information is neither sold nor rented to third parties
      without your express permission. We do retain the right to share
      non-identifying aggregated demographic and/or statistical information with
      partners, advertisers and sponsors. We may use non-identifying and
      aggregated information to improve your experience on the website and to
      share with partners, advertisers and sponsors. For example, any personal
      information will remain with us, however, your visit may be counted to
      demonstrate the number of visitors to a particular page or area of the
      site.
    </p>

    <h4>Links to Third Party</h4>

    <p>
      Sites Our Platform includes links to other Web sites whose privacy
      practices may differ from those of Carson. If you submit personal
      information to any of those sites, your information is governed by their
      privacy policies. We encourage you to carefully read the privacy policy of
      any Web site you visit.
    </p>

    <h4>Legal Disclaimer</h4>

    <p>We may disclose your personal information:</p>

    <ul>
      <li>
        <p>
          As required by law, such as to comply with a subpoena, or similar
          legal process
        </p>
      </li>
      <li>
        <p>
          When we believe in good faith that disclosure is necessary to protect
          our rights, protect your safety or the safety of others, investigate
          fraud, or respond to a government request
        </p>
      </li>
      <li>
        <p>
          If Carson is involved in a merger, acquisition, or sale of all or a
          portion of its assets, you will be notified via email and/or a
          prominent notice on our Web site of any change in ownership or uses of
          your personal information, as well as any choices you may have
          regarding your personal information
        </p>
      </li>
    </ul>

    <p>
      Carson will only retain personal data we process on behalf of our
      Customers for as long as needed to provide services and as necessary to
      comply with our legal obligations, resolve disputes and enforce our
      agreements.
    </p>

    <h4>User Access and Choice</h4>

    <p>
      Users residing in certain countries, including in Europe, are afforded
      certain rights regarding their personal information. These rights include
      the right to access, correct and request deletion of your personal
      information. While these rights are not applicable worldwide, all Carson
      Registered Users can manage their personal information as described below.
      <br />
      <br />
      You may access, correct, update, amend or remove certain personal
      information by making the change on your user account settings page or by
      requesting that your property manager (or other program administrator)
      make such change on your behalf. Your account is protected by a password.
      We encourage you to choose your password carefully and keep it secure. If
      you have trouble accessing your account or your information, you can
      contact Carson by emailing our customer support team at&nbsp;
      <a href="mailto:support@carson.live" target="_self">
        support@carson.live
      </a>
      .<h4>Opt-out Choices</h4>
      Carson offers you choices regarding the collection, use and sharing of
      your personal information. Under the Privacy Shield Principles, you have
      the right to opt-out of i) disclosures of your personal information to
      third party vendors not identified at the time of collection or
      subsequently authorized, or ii) uses of your personal information since
      you are not required to provide any personal data. If you no longer
      require our services, or wish to request your personal information be
      deleted/removed, please contact us using the aforementioned direct
      methods. Termination of service proceedings are conducted offline and are
      subject to approval by our Customer, if you are a resident or staff user.
      We will respond to your request within thirty (30) days.
    </p>

    <h4>Our commitment to children's privacy</h4>

    <p>
      Protecting the privacy of the very young is especially important. For that
      reason, we never collect or maintain information at our website from those
      we actually know are under 13, and no part of our website is structured to
      attract anyone under 13.
    </p>

    <h4>The EU-U.S. Privacy Shield</h4>

    <p>
      Carson complies with the E.U.-U.S. Privacy Shield Framework as set forth
      by the U.S. Department of Commerce regarding the collection, use and
      retention of personal information from the European Union. Carson has
      certified to the Department of Commerce that it adheres to the Privacy
      Shield Principles. If there is any conflict between this Privacy Policy
      and the Privacy Shield Principles, the Privacy Shield Principles shall
      govern. To learn more about the E.U.-U.S. Privacy Shield, and to view our
      certification, please visit{' '}
      <a
        data-auto-recognition="true"
        href="http://www.privacyshield.gov"
        target="_blank"
        rel="noreferrer"
      >
        www.privacyshield.gov
      </a>
      . questions, comments, complaints, access requests or other issues
      relating to the Privacy Shield or our Privacy Policy, please email{' '}
      <a data-auto-recognition="true" href="mailto:privacy@carson.live">
        privacy@carson.live
      </a>{' '}
      or call{' '}
      <a href="tel:+1 (646) 661-2250" target="_self">
        +1 (646) 661-2250
      </a>
      . For any issues that cannot be resolved through Carson directly, you may
      contact JAMS at{' '}
      <a
        data-auto-recognition="true"
        href="https://www.jamsadr.com/eu-us-privacy-shield."
        target="_blank"
        rel="noreferrer"
      >
        https://www.jamsadr.com/eu-us-privacy-shield.
      </a>{' '}
      If your concerns are still not addressed by JAMS, you may be entitled to a
      binding arbitration under the Privacy Shield Principles. The Federal Trade
      Commission (FTC) has jurisdiction over Carson's compliance with the
      Privacy Shield.
    </p>

    <h4>Data Protection Addendum&nbsp;</h4>

    <p>
      To assist our customers in the EEA in complying with applicable data
      protection legislation, we offer a Data Processing Addendum (DPA) to our
      Subscription Agreement. To request a DPA, contact us at{' '}
      <a data-auto-recognition="true" href="mailto:privacy@carson.live">
        privacy@carson.live
      </a>
      .
    </p>

    <h4>Data Protection Officer</h4>

    <p>
      To contact our Data Protection Officer, please email{' '}
      <a data-auto-recognition="true" href="mailto:privacy@carson.live">
        privacy@carson.live
      </a>
      .&nbsp;
    </p>

    <h4>Changes to this policy</h4>

    <p>
      We may update this privacy policy to reflect changes to our information
      practices. If we make any material changes, we will notify you by email
      (sent to the e-mail address specified in your account or by means of a
      notice on this page prior to the change becoming effective. We encourage
      you to periodically review this page for the latest information on our
      privacy
    </p>

    <p>
      <h4>Contact Us</h4>
      Postal&nbsp; &nbsp; &nbsp; &nbsp; &nbsp; 88 University Pl., 10th Floor
    </p>

    <p>Address&nbsp; &nbsp; &nbsp; New York, N.Y. 10003 &nbsp;</p>

    <p>
      Phone&nbsp; &nbsp; &nbsp; &nbsp; &nbsp;{' '}
      <a href="tel:+1 (646) 661-2250" target="_self">
        +1 (646) 661-2250
      </a>
      <br />
      Email&nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp;
      <a href="mailto:support@carson.live" target="_self">
        support@carson.live
      </a>
    </p>

    <div>
      &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp;
      &nbsp;
      <a data-auto-recognition="true" href="mailto:privacy@carson.live">
        privacy@carson.live
      </a>
    </div>
  </div>
);

export default PrivacyPolicy;
